import {AppleLogoWhite, PlayStore} from '@jobseeker/lotus/icons';
import {JobdetailQr} from '@jobseeker/lotus/images';
import {
  Box,
  Typography as Typo,
  styled,
  TypographyProps,
  Card,
} from '@mui/material';
import {
  getAppDownloadLinkagesUrls,
  openUrl,
} from '@jobseeker/common-core/utils/common';
import {Image} from '@jobseeker/lotus/components/Image';
import {useCallback, useEffect} from 'react';
import {handleTracking, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';

const APP_LINKS = {
  iimjobs: {
    android: 'https://play.google.com/store/apps/details?id=com.org.iimjobs',
    apple:
      'https://apps.apple.com/in/app/iimjobs-management-job-search/id538258273',
    qr: JobdetailQr,
  },
  hirist: {
    android:
      'https://play.google.com/store/apps/details?id=com.hirist.jobseeker&hl=en_IN',
    apple:
      'https://apps.apple.com/in/app/hirist-tech-it-job-search-app/id952697960',
    qr: 'https://static.iimjobs.com/resources/images/hirist_app_link.svg',
  },
};

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

type ButtonProps = {
  logoSrc: string;
  logoAlt: string;
  first: string;
  second: string;
  forPlayStore: boolean;
  imgWidth: number;
  imgHeight: number;
  handleClick: () => void;
};

const ButtonFun: React.FC<ButtonProps> = ({
  logoSrc,
  logoAlt,
  first,
  second,
  forPlayStore,
  imgWidth,
  imgHeight,
  handleClick,
}) => {
  return (
    <Box
      onClick={handleClick}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      borderRadius={2}
      p={0.75}
      pl={forPlayStore ? 0.875 : 0.75}
      pr={1.875}
      sx={{backgroundColor: 'text.primary', cursor: 'pointer'}}
    >
      <Box sx={{marginRight: '4px'}} display={'flex'}>
        <Image
          src={logoSrc}
          alt={logoAlt}
          width={imgWidth}
          height={imgHeight}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'flex-start'}
      >
        <Typography
          variant="body2"
          fontSize={6}
          color={'#fff'}
          lineHeight={'8px'}
        >
          {first}
        </Typography>
        <Typography
          variant="subtitle2"
          fontSize={9}
          color={'#fff'}
          lineHeight={'8px'}
        >
          {second}
        </Typography>
      </Box>
    </Box>
  );
};

interface DownloadAppOnJobdetailProps {
  sendEvent?: (
    eventCatogory: string,
    eventName: string,
    action: string
  ) => void;
  showWidgetMobile?: boolean;
}

const DownloadAppOnJobdetail = ({
  sendEvent = (eventCatogory: string, eventName: string, action: string) => {},
  showWidgetMobile = false,
}: DownloadAppOnJobdetailProps) => {
  const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist';
  const isAppIimjobs = PLATFORM === 'iimjobs';

  const router = useRouter();

  const handleUbaTracking = (action = 'view') => {
    const trackingData = {
      pageName: 'Login',
      linkageName: 'Landing Page Download App Banner',
      linkageType: 'bottomsheetBanner',
      sessionUsername: sessionStorage.getItem('userId') || '',
      ...(action === 'click' && {typeClick: 'CTA'}),
    };
    TrackEventUBA(
      action === 'click' ? 'appDownloadLinkageClick' : 'appDownloadLinkageView',
      action,
      trackingData
    );
  };

  const sendViewMobileAppLinkageEvents = useCallback(() => {
    const gaEventData = {
      event_category: 'CallOuts',
      Origin: router.pathname,
      Device: sessionStorage.getItem('device') || 'desktop',
      UserId: sessionStorage.getItem('userId') || -1,
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    };

    handleTracking('jsViewMobileAppLinkage', {...gaEventData, Type: 'iOS'});
    handleTracking('jsViewMobileAppLinkage', {...gaEventData, Type: 'Android'});
  }, []);

  useEffect(() => {
    if (isAppIimjobs) {
      return;
    }
    showWidgetMobile && handleUbaTracking('view');
    sendViewMobileAppLinkageEvents();
  }, []);

  return (
    <>
      <Card
        data-testid="download_app_qr_on_jobdetail"
        sx={{
          border: showWidgetMobile ? '1px solid' : 'none',
          borderColor: showWidgetMobile ? 'primary.main' : 'transparent',
          backgroundColor: showWidgetMobile ? '#FFFAF0' : '#fff',
          borderRadius: isAppIimjobs ? 4 : 2,
          marginTop: '12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 1.5,
          pr: 2.25,
          '@media (max-width: 1024px)': {
            display: !isAppIimjobs && !showWidgetMobile ? 'none' : 'flex',
          },
          '@media (min-width: 1025px) and (max-width: 1279px)': {
            display: !isAppIimjobs ? 'flex' : 'none', // Show only if width is between 1024px and 1279px
          },
          '@media (min-width: 1280px)': {
            display: 'flex', // Hide above 1279px
          },
          [`@media (max-width: ${isAppIimjobs ? 1279 : 900}px)`]: {
            display: !showWidgetMobile ? 'none' : 'flex',
          },
        }}
      >
        <Box
          width={'100%'}
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          onClick={() => {
            if (!showWidgetMobile) return;
            handleUbaTracking('click');
            openUrl(getAppDownloadLinkagesUrls(1), '_blank');
          }}
        >
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex:start'}
            mr={0.875}
          >
            <Typography
              variant={showWidgetMobile ? 'inherit' : 'body3'}
              color={'text.secondary'}
              lineHeight={'16px'}
              fontSize={showWidgetMobile ? 14 : 12}
              mb={showWidgetMobile ? 1 : 0}
            >
              Apply on the go!
            </Typography>
            <Typography
              mt={0.25}
              variant="subtitle2"
              color={'text.primary'}
              lineHeight={showWidgetMobile ? '21px' : '16px'}
              fontSize={showWidgetMobile ? 16 : 14}
              fontWeight={showWidgetMobile ? 700 : 500}
            >
              {`Download the ${PLATFORM} app to`}
              {showWidgetMobile ? '' : <br />} apply for jobs anywhere, anytime
            </Typography>
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              gap={0.5}
              mt={1.25}
            >
              <ButtonFun
                handleClick={() => {
                  if (showWidgetMobile) return;
                  sendEvent('App_Promotion', 'DownloadAppQR1Desktop', 'click');
                  openUrl(APP_LINKS[PLATFORM].apple, '_blank');
                }}
                logoSrc={AppleLogoWhite}
                logoAlt="apple"
                first="Download on"
                second="App Store"
                imgWidth={16}
                forPlayStore={false}
                imgHeight={16}
              />
              <ButtonFun
                handleClick={() => {
                  if (showWidgetMobile) return;
                  sendEvent('App_Promotion', 'DownloadAppQR1Desktop', 'Click');
                  openUrl(APP_LINKS[PLATFORM].android, '_blank');
                }}
                logoSrc={PlayStore}
                logoAlt="playStore"
                first="Get it on"
                second="Google Play"
                forPlayStore={true}
                imgWidth={14}
                imgHeight={14}
              />
            </Box>
          </Box>
          {!showWidgetMobile && (
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Box
                borderRadius={2}
                p={0.5}
                display={'flex'}
                border={'1px solid rgba(126, 126, 126, 0.20)'}
              >
                <Image
                  src={APP_LINKS[PLATFORM].qr}
                  width={52}
                  height={52}
                  alt="appPromoQr"
                  style={{borderRadius: 4}}
                />
              </Box>
              <Typography
                mt={0.25}
                width={'64px'}
                fontSize={7}
                fontWeight={500}
                lineHeight={'10px'}
                color={'text.primary'}
              >
                Scan to Download
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};

export default DownloadAppOnJobdetail;
